/**
 * Company onboarding helpers.
 * Azure SQL APIs only.
 * SetupWizard runs with azureOnly=true and talks to /api/companies/*.
 */

export type CompanyStatus = "PendingSetup" | "Active" | "Suspended" | "Archived";

export interface CompanyDoc {
  id: string;
  name: string;
  companyCode: string;
  status: CompanyStatus;
  address?: string;
  postalAddress?: string;
  logoUrl?: string;
  /** Invite / primary contact email — used to resume onboarding for the person completing setup. */
  contactEmail?: string;
  companyEmail?: string;
  setupCompletedAt?: any;
  setupCompletedBy?: string | null;
  createdAt: any;
  createdBy: string;
  updatedAt: any;
  updatedBy: string;
}

const AZURE_API_ONLY = "Use Azure SQL API routes.";

export async function ensureCompanyForCode(_options: {
  companyCode: string;
  currentUserId: string;
  currentUserName?: string;
}): Promise<CompanyDoc | null> {
  return null;
}

export async function getCompanyByCode(_companyCode: string): Promise<CompanyDoc | null> {
  return null;
}

export async function validateCompanySetup(companyId: string) {
  try {
    const apiRes = await fetch(`/api/companies/${encodeURIComponent(companyId)}/validate-setup`);
    if (apiRes.ok) {
      const data = await apiRes.json().catch(() => ({} as {
        isValid?: boolean;
        issues?: {
          missingCycles?: boolean;
          missingCompanyStandards?: boolean;
          missingFramework?: boolean;
          missingGradingScale?: boolean;
          missingRoleMappings?: boolean;
        };
      }));
      const issues = data.issues ?? {};
      const normalized = {
        missingCycles: !!issues.missingCycles,
        missingCompanyStandards: !!issues.missingCompanyStandards,
        missingFramework: !!issues.missingFramework,
        missingGradingScale: !!issues.missingGradingScale,
        missingRoleMappings: !!issues.missingRoleMappings,
      };
      return {
        isValid:
          typeof data.isValid === "boolean"
            ? data.isValid
            : (!normalized.missingCycles &&
                !normalized.missingCompanyStandards &&
                !normalized.missingFramework &&
                !normalized.missingGradingScale),
        issues: normalized,
      };
    }
  } catch {
    /* Azure SQL API is the only path */
  }

  return {
    isValid: false,
    issues: {
      missingCycles: true,
      missingCompanyStandards: true,
      missingFramework: true,
      missingGradingScale: true,
      missingRoleMappings: false,
    },
  };
}

export async function applyStandardCycle(_options: {
  companyId: string;
  cycleId: string;
  cycleName: string;
  userId: string;
}): Promise<{ success: boolean; error?: string }> {
  return { success: false, error: AZURE_API_ONLY };
}

export interface CompanyJobRole {
  id: string;
  name: string;
  requiresCompetency: boolean;
  isDeleted?: boolean;
}

export async function getCompanyJobRoles(_companyId: string): Promise<CompanyJobRole[]> {
  return [];
}

export async function saveCompanyJobRoles(_options: {
  companyId: string;
  roles: { name: string; requiresCompetency: boolean }[];
  userId: string;
}): Promise<{ success: boolean; error?: string }> {
  return { success: false, error: AZURE_API_ONLY };
}

export interface CompanyFramework {
  id?: string;
  cycleId?: string;
  name?: string;
  isDeleted?: boolean;
}

export async function getCompanyFrameworks(companyId: string): Promise<CompanyFramework[]> {
  if (!companyId) return [];
  try {
    const res = await fetch(`/api/companies/${encodeURIComponent(companyId)}/frameworks`);
    if (!res.ok) return [];
    const data = await res.json().catch(() => []);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export interface CompanyGradingScale {
  id: string;
  name: string;
  description: string;
  isCustom: boolean;
}

function slugForId(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "item";
}

/** Stable id for a role↔cycle mapping (one doc per pair). */
export function roleMappingDocId(jobRoleName: string, cycleId: string): string {
  const a = slugForId(jobRoleName);
  const b = slugForId(cycleId);
  return `${a}__${b}`.slice(0, 700);
}

export async function getCompanyGradingScales(_companyId: string): Promise<CompanyGradingScale[]> {
  return [];
}

export async function saveCompanyGradingScales(_options: {
  companyId: string;
  grades: { name: string; description: string; isCustom?: boolean }[];
  userId: string;
  userEmail?: string;
}): Promise<{ success: boolean; error?: string }> {
  return { success: false, error: AZURE_API_ONLY };
}

export interface CompanyRoleMapping {
  id?: string;
  jobRoleName: string;
  cycleId: string;
  cycleName?: string;
  cycleLengthYears?: number;
  isDeleted?: boolean;
}

export async function getCompanyRoleMappings(_companyId: string): Promise<CompanyRoleMapping[]> {
  return [];
}

export async function saveCompanyRoleMappings(_options: {
  companyId: string;
  mappings: { jobRoleName: string; cycleId: string; cycleName?: string; cycleLengthYears?: number }[];
  userId: string;
  userEmail?: string;
}): Promise<{ success: boolean; error?: string }> {
  return { success: false, error: AZURE_API_ONLY };
}

export async function saveCompanyLogo(_options: {
  companyId: string;
  logoDataUrl: string;
  fileName?: string;
  userId: string;
  userEmail?: string;
}): Promise<{ success: boolean; logoUrl?: string; error?: string }> {
  return { success: false, error: AZURE_API_ONLY };
}

export async function activateCompanySetup(options: {
  companyId: string;
  userId: string;
  userEmail?: string;
}): Promise<{
  success: boolean;
  error?: string;
  nominatedAdminProvisioning?: { provisioned?: boolean; emailed?: boolean; skipped?: boolean; warning?: string };
}> {
  const { companyId, userId, userEmail } = options;
  const actor = userEmail || userId;

  try {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (userEmail) headers["X-User-Email"] = userEmail;
    const apiRes = await fetch(`/api/companies/${encodeURIComponent(companyId)}/activate`, {
      method: "POST",
      headers,
      body: JSON.stringify({ userEmail: actor }),
    });
    const apiData = await apiRes.json().catch(() => ({} as { success?: boolean; error?: string; nominatedAdminProvisioning?: unknown }));
    if (apiRes.ok && apiData.success !== false) {
      return {
        success: true,
        nominatedAdminProvisioning:
          typeof apiData.nominatedAdminProvisioning === "object" && apiData.nominatedAdminProvisioning !== null
            ? (apiData.nominatedAdminProvisioning as {
                provisioned?: boolean;
                emailed?: boolean;
                skipped?: boolean;
                warning?: string;
              })
            : undefined,
      };
    }
    if (!apiRes.ok) {
      return { success: false, error: apiData.error || `Activation failed (${apiRes.status})` };
    }
  } catch (e: any) {
    return { success: false, error: e?.message || AZURE_API_ONLY };
  }

  return { success: false, error: AZURE_API_ONLY };
}

export async function saveCompanySetupWizardStep(_options: {
  companyId: string;
  step: string;
  userId?: string;
  userEmail?: string;
}): Promise<void> {
  return;
}

export async function markCompanyContactPasswordChanged(_options: {
  companyId: string;
  userId: string;
  userEmail?: string;
}): Promise<void> {
  return;
}
